import PropTypes from 'prop-types'
import React from 'react'

import FormFieldBase from '@components/forms/form_field_base'

import HidePasswordIcon from './children/hide_password_icon'
import ShowPasswordIcon from './children/show_password_icon'
import { EMAIL_BLACKLIST } from './constants'
import { isAutoFillEvent, getInputType, getAriaLabelledBy } from './helpers'
import * as styles from './styles'

class InputField extends FormFieldBase {
  constructor(props) {
    super(props)
    this.state = {
      focused: false,
      showPassword: false,
      showPasswordFocus: false,
      autoFill: false
    }
    this.inputRef = React.createRef()
    this.bindHandlers()
  }

  bindHandlers() {
    this.handleChange = this.handleChange.bind(this)
    this.handleShowPasswordClick = this.handleShowPasswordClick.bind(this)
    this.handleShowPasswordFocus = this.handleShowPasswordFocus.bind(this)
    this.handleShowPasswordBlur = this.handleShowPasswordBlur.bind(this)
    this.handleAnimationStart = this.handleAnimationStart.bind(this)
  }

  handleAnimationStart(e) {
    this.setState({ autoFill: isAutoFillEvent(e) })
  }

  handleChange(event) {
    const newValue = event.target.value
    if (newValue.length <= this.props.maxLength) this.processChange(newValue)
  }

  handleShowPasswordClick() {
    this.setState({ showPassword: !this.state.showPassword })
  }

  handleShowPasswordFocus() {
    this.setState({ showPasswordFocus: true })
  }

  handleShowPasswordBlur() {
    this.setState({ showPasswordFocus: false })
  }

  renderInput() {
    return (
      <input
        ref={this.props.passRef || this.inputRef}
        css={[
          styles.inputStyles,
          this.props.type === 'password' && styles.inputPasswordStyles
        ]}
        id={this.props.id}
        autoComplete={this.props.autoComplete}
        name={this.props.inputName}
        type={getInputType(this.props, this.state)}
        value={this.props.value}
        onChange={this.handleChange}
        onFocus={this.handleFocus}
        onBlur={this.handleBlur}
        aria-labelledby={getAriaLabelledBy(this.props)}
        onAnimationStart={this.handleAnimationStart}
        disabled={this.props.disabled}
        data-testid={this.props.testId}
        placeholder={this.props.placeholder}
      />
    )
  }

  renderShowPassword() {
    if (this.props.type !== 'password') return

    return (
      <button
        type='button'
        css={styles.showPasswordStyles}
        onClick={this.handleShowPasswordClick}
        onFocus={this.handleShowPasswordFocus}
        onBlur={this.handleShowPasswordBlur}
        aria-pressed={this.state.showPassword}
        aria-label={this.props.showPasswordAriaLabel}
      >
        {this.state.showPassword ? (
          <HidePasswordIcon
            style={styles.showPasswordIconStyles}
            focused={this.state.showPasswordFocus}
          />
        ) : (
          <ShowPasswordIcon
            style={styles.showPasswordIconStyles}
            focused={this.state.showPasswordFocus}
          />
        )}
      </button>
    )
  }

  render() {
    const { consumerGroup } = this.props
    return (
      <div css={styles.containerStyles}>
        {this.renderInputLabel()}
        {this.renderSubLabel()}
        {this.renderInput()}
        {this.renderInputError(consumerGroup)}
        {this.renderInputWarning()}
        {this.renderShowPassword()}
        {this.renderHint()}
      </div>
    )
  }
}

InputField.EmailBlacklistValidation = (email) => {
  const EMAIL_DOMAIN = email.split('@')

  return EMAIL_BLACKLIST.indexOf(EMAIL_DOMAIN[1]) >= 0
}

InputField.EmailValidation = (value) => {
  const EMAIL_REGEX = /^([^@\s]+)@((?:[-a-z0-9]+\.)+[a-z]{2,})$/
  return !EMAIL_REGEX.test(value)
}

// Matches https://git.thebeansgroup.com/studentbeans/auth_engine/blob/master/app/models/users/auth/user.rb#L108
InputField.EducationalEmailValidation = (value) => {
  const EMAIL_REGEX = /^.*\.ac\.uk$/
  return EMAIL_REGEX.test(value)
}

InputField.RequiredValidation = (value) => {
  const REQUIRED_REGEX = /^\s*$/
  return REQUIRED_REGEX.test(value)
}

InputField.defaultProps = {
  focusHandler: () => {},
  blurHandler: () => {},
  validations: [],
  autoComplete: 'off',
  type: 'text',
  ariaLabelledBy: '',
  placeholder: '',
  maxLength: 99999,
  disabled: false,
  consumerGroup: 'student'
}

InputField.propTypes = {
  value: PropTypes.string.isRequired,
  label: PropTypes.string,
  optional: PropTypes.bool,
  subLabel: PropTypes.string,
  id: PropTypes.string.isRequired,
  type: PropTypes.string,
  inputName: PropTypes.string.isRequired,
  focusHandler: PropTypes.func,
  blurHandler: PropTypes.func,
  valueHandler: PropTypes.func.isRequired,
  error: PropTypes.string,
  errorAction: PropTypes.func,
  errorActionText: PropTypes.string,
  autoComplete: PropTypes.string,
  hint: PropTypes.string,
  ariaLabelledBy: PropTypes.string,
  placeholder: PropTypes.string,
  showPasswordAriaLabel: PropTypes.string,
  maxLength: PropTypes.number,
  disabld: PropTypes.bool,
  consumerGroup: PropTypes.string
}

export default InputField
